import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  Option,
  Select,
  Sheet,
  Stack,
  Table,
  Tooltip,
  Typography,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import StarIcon from "@mui/icons-material/Star";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import { useLocation, useNavigate } from "react-router-dom";

import { useModels } from "../../hooks/useModels";
import CreateModelModal from "./modals/CreateModelModal";
import EditModelModal from "./modals/EditModelModal";
import DeleteModelModal from "./modals/DeleteModelModal";
import {
  getProviderColor,
  getProviderKind,
  getProviderLabel,
  KNOWN_PROVIDER_OPTIONS,
  normalizeProviderKey,
} from "../../helpers/modelProviders";

const KIND_OPTIONS = [
  { value: "", label: "All Kinds" },
  { value: "llm", label: "LLM" },
  { value: "vision", label: "Vision" },
];

const KIND_COLORS = {
  llm: "primary",
  vision: "warning",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "true", label: "Active" },
  { value: "false", label: "Inactive" },
];

const ModelsTab = ({ readOnly = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    models,
    loading,
    fetchModels,
    createModel,
    updateModel,
    deleteModel,
    setDefaultModel,
    discoverModels,
    toggleModelActive,
    providers,
    providersLoading,
  } = useModels();
  const hasAutoOpenedCreateModalRef = useRef(false);
  const onboardingMode = useMemo(
    () => new URLSearchParams(location.search).get("onboarding") === "1",
    [location.search]
  );

  // Filter states
  const [search, setSearch] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [kindFilter, setKindFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const providerFilterOptions = useMemo(() => {
    const optionsMap = new Map();

    const addOption = (providerValue, providerLabel) => {
      const normalizedValue = normalizeProviderKey(providerValue);
      if (!normalizedValue || optionsMap.has(normalizedValue)) {
        return;
      }

      optionsMap.set(normalizedValue, {
        value: normalizedValue,
        label: providerLabel || getProviderLabel(providerValue),
      });
    };

    (Array.isArray(providers) ? providers : []).forEach((provider) => {
      if (typeof provider === "string") {
        addOption(provider);
        return;
      }

      addOption(provider?.value, provider?.label);
    });

    if (optionsMap.size === 0) {
      KNOWN_PROVIDER_OPTIONS.forEach((provider) => addOption(provider.value, provider.label));
    }

    const sortedOptions = [...optionsMap.values()].sort((a, b) => a.label.localeCompare(b.label));
    return [{ value: "", label: "All Providers" }, ...sortedOptions];
  }, [providers]);

  // Pagination states
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [page, setPage] = useState(0);

  // Modal states
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(null);

  // Filter models
  const filteredModels = useMemo(() => {
    return models.filter((model) => {
      const searchLower = search.toLowerCase();
      const matchesSearch =
        !search ||
        model.name?.toLowerCase().includes(searchLower) ||
        model.display_name?.toLowerCase().includes(searchLower) ||
        model.provider?.toLowerCase().includes(searchLower) ||
        model.kind?.toLowerCase().includes(searchLower);

      const modelKind = model.kind || getProviderKind(model.provider);
      const matchesProvider = !providerFilter || normalizeProviderKey(model.provider) === providerFilter;
      const matchesKind = !kindFilter || modelKind === kindFilter;

      const matchesStatus =
        !statusFilter ||
        (statusFilter === "true" ? model.is_active : !model.is_active);

      return matchesSearch && matchesProvider && matchesKind && matchesStatus;
    });
  }, [models, search, providerFilter, kindFilter, statusFilter]);

  // Paginate models
  const paginatedModels = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredModels.slice(start, start + rowsPerPage);
  }, [filteredModels, page, rowsPerPage]);

  // Handlers
  const handleOpenEdit = (model) => {
    setSelectedModel(model);
    setEditModalOpen(true);
  };

  const handleOpenDelete = (model) => {
    setSelectedModel(model);
    setDeleteModalOpen(true);
  };

  const handleCloseModals = () => {
    setSelectedModel(null);
    setEditModalOpen(false);
    setDeleteModalOpen(false);
  };

  const handleChangeRowsPerPage = (newValue) => {
    const updatedValue = Number(newValue);
    if (updatedValue > 0) {
      setRowsPerPage(updatedValue);
      setPage(0);
    }
  };

  const handleChangePage = (newPage) => {
    setPage(newPage);
  };

  const handleRefresh = () => {
    fetchModels();
  };

  const handleToggleActive = async (model) => {
    await toggleModelActive(model.id, !model.is_active);
  };

  const handleSetDefault = async (model) => {
    await setDefaultModel(model.id);
  };

  const handleCreateModel = async (payload) => {
    const result = await createModel(payload);
    if (result.success && onboardingMode) {
      navigate("/app/agents?onboarding=1&new=1", { replace: true });
    }
    return result;
  };

  const labelDisplayedRows = ({ from, to, count }) =>
    `${from}-${to} of ${count}`;

  const totalPages = Math.ceil(filteredModels.length / rowsPerPage);

  useEffect(() => {
    if (!onboardingMode) {
      hasAutoOpenedCreateModalRef.current = false;
      return;
    }

    if (readOnly || loading || models.length > 0 || hasAutoOpenedCreateModalRef.current) {
      return;
    }

    hasAutoOpenedCreateModalRef.current = true;
    setCreateModalOpen(true);
  }, [loading, models.length, onboardingMode, readOnly]);

  if (loading && models.length === 0) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography level="title-lg" fontWeight="bold" mb={2}>
        Models
      </Typography>

      {/* Toolbar */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={1}
          alignItems={{ xs: "stretch", sm: "center" }}
          sx={{ flex: 1 }}
        >
          <Input
            placeholder="Search models..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(0);
            }}
            startDecorator={<SearchIcon />}
            sx={{ minWidth: 200 }}
          />

          <Select
            value={providerFilter}
            onChange={(_, value) => {
              setProviderFilter(value);
              setPage(0);
            }}
            sx={{ minWidth: 150 }}
          >
            {providerFilterOptions.map((provider) => (
              <Option key={provider.value} value={provider.value}>
                {provider.label}
              </Option>
            ))}
          </Select>

          <Select
            value={kindFilter}
            onChange={(_, value) => {
              setKindFilter(value);
              setPage(0);
            }}
            sx={{ minWidth: 130 }}
          >
            {KIND_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>

          <Select
            value={statusFilter}
            onChange={(_, value) => {
              setStatusFilter(value);
              setPage(0);
            }}
            sx={{ minWidth: 130 }}
          >
            {STATUS_OPTIONS.map((option) => (
              <Option key={option.value} value={option.value}>
                {option.label}
              </Option>
            ))}
          </Select>

          <Tooltip title="Refresh">
            <IconButton onClick={handleRefresh} variant="outlined">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>

        {!readOnly && (
          <Button
            variant="soft"
            startDecorator={<AddIcon />}
            onClick={() => setCreateModalOpen(true)}
          >
            Add Model
          </Button>
        )}
      </Stack>

      {/* Models Table */}
      {filteredModels.length === 0 ? (
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            bgcolor: "background.level1",
            borderRadius: 2,
          }}
        >
          <Typography level="body-lg" color="neutral" sx={{ mb: 1 }}>
            {models.length === 0 ? "No models configured" : "No models match your filters"}
          </Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
              {models.length === 0
              ? "Get started by adding your first model."
              : "Try adjusting your search or filter criteria."}
            </Typography>
          {models.length === 0 && !readOnly && (
            <Button
              variant="soft"
              startDecorator={<AddIcon />}
              onClick={() => setCreateModalOpen(true)}
            >
              Add Your First Model
            </Button>
          )}
        </Box>
      ) : (
        <Sheet
          variant="outlined"
          sx={{
            width: "100%",
            borderRadius: "sm",
            overflow: "auto",
          }}
        >
          <Table
            aria-label="models table"
            stickyHeader
            hoverRow
            sx={{
              "--TableCell-headBackground": "var(--joy-palette-background-level1)",
              "--Table-headerUnderlineThickness": "1px",
              "--TableRow-hoverBackground": "var(--joy-palette-background-level1)",
              "--TableCell-paddingY": "8px",
              "--TableCell-paddingX": "12px",
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 60 }}>ID</th>
                <th style={{ width: 200 }}>Name</th>
                <th style={{ width: 180 }}>Display Name</th>
                <th style={{ width: 120 }}>Provider</th>
                <th style={{ width: 100 }}>Kind</th>
                <th style={{ width: 80 }}>Default</th>
                <th style={{ width: 80 }}>Status</th>
                {!readOnly && <th style={{ width: 100 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {paginatedModels.map((model) => {
                const modelKind = model.kind || getProviderKind(model.provider) || "unknown";

                return (
                <tr key={model.id}>
                  <td>
                    <Typography level="body-sm">{model.id}</Typography>
                  </td>
                  <td>
                    <Typography level="body-sm" fontWeight="md" sx={{ wordBreak: "break-all" }}>
                      {model.name}
                    </Typography>
                  </td>
                  <td>
                    <Typography level="body-sm">
                      {model.display_name || "-"}
                    </Typography>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={getProviderColor(model.provider)}
                    >
                      {getProviderLabel(model.provider)}
                    </Chip>
                  </td>
                  <td>
                    <Chip
                      size="sm"
                      variant="soft"
                      color={KIND_COLORS[modelKind] || "neutral"}
                    >
                      {modelKind.toUpperCase()}
                    </Chip>
                  </td>
                  <td>
                    <Tooltip title="Default model">
                      <IconButton
                        size="sm"
                        variant="plain"
                        color={model.is_default ? "warning" : "neutral"}
                        onClick={() => !readOnly && !model.is_default && handleSetDefault(model)}
                        disabled={readOnly || model.is_default}
                      >
                        {model.is_default ? <StarIcon /> : <StarOutlineIcon />}
                      </IconButton>
                    </Tooltip>
                  </td>
                  <td>
                    <Tooltip title={model.is_active ? "Active" : "Inactive"}>
                      <IconButton
                        size="sm"
                        variant="plain"
                        color={model.is_active ? "success" : "neutral"}
                        onClick={() => !readOnly && handleToggleActive(model)}
                        disabled={readOnly}
                      >
                        {model.is_active ? <CheckCircleIcon /> : <CancelIcon />}
                      </IconButton>
                    </Tooltip>
                  </td>
                  {!readOnly && (
                    <td>
                      <Box sx={{ display: "flex", gap: 0.5 }}>
                        <Tooltip title="Edit model">
                          <IconButton
                            size="sm"
                            variant="plain"
                            onClick={() => handleOpenEdit(model)}
                          >
                            <EditIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete model">
                          <IconButton
                            size="sm"
                            variant="plain"
                            color="danger"
                            onClick={() => handleOpenDelete(model)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={readOnly ? 7 : 8}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      justifyContent: "flex-end",
                      py: 1,
                    }}
                  >
                    <FormControl orientation="horizontal" size="sm">
                      <FormLabel>Rows per page:</FormLabel>
                      <Select
                        value={rowsPerPage}
                        onChange={(_, newValue) => handleChangeRowsPerPage(newValue)}
                        size="sm"
                      >
                        <Option value={5}>5</Option>
                        <Option value={10}>10</Option>
                        <Option value={25}>25</Option>
                        <Option value={50}>50</Option>
                      </Select>
                    </FormControl>
                    <Typography sx={{ textAlign: "center", minWidth: 80 }}>
                      {labelDisplayedRows({
                        from: filteredModels.length === 0 ? 0 : page * rowsPerPage + 1,
                        to: Math.min(filteredModels.length, (page + 1) * rowsPerPage),
                        count: filteredModels.length,
                      })}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 0.5 }}>
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={page === 0}
                        onClick={() => handleChangePage(page - 1)}
                      >
                        <KeyboardArrowLeftIcon />
                      </IconButton>
                      <IconButton
                        size="sm"
                        variant="outlined"
                        color="neutral"
                        disabled={page >= totalPages - 1}
                        onClick={() => handleChangePage(page + 1)}
                      >
                        <KeyboardArrowRightIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </td>
              </tr>
            </tfoot>
          </Table>
        </Sheet>
      )}

      {/* Modals */}
      <CreateModelModal
        open={createModalOpen}
        onClose={() => setCreateModalOpen(false)}
        onCreateModel={handleCreateModel}
        onDiscoverModels={discoverModels}
        providers={providers}
        providersLoading={providersLoading}
      />

      <EditModelModal
        open={editModalOpen}
        onClose={handleCloseModals}
        model={selectedModel}
        onUpdateModel={updateModel}
      />

      <DeleteModelModal
        open={deleteModalOpen}
        onClose={handleCloseModals}
        model={selectedModel}
        onConfirmDelete={deleteModel}
      />
    </>
  );
};

export default ModelsTab;
